import { useTranslation } from 'react-i18next';
import { Check, Circle } from 'lucide-react';
import { PASSWORD_RULES } from '../../utils/password';

interface PasswordCriteriaProps {
  password: string;
  className?: string;
}

/**
 * Live checklist of the backend password policy. Each rule turns green with a
 * check as soon as the typed password satisfies it. Rules and their order come
 * from {@link PASSWORD_RULES} so display and validation never drift apart.
 */
export function PasswordCriteria({ password, className = '' }: PasswordCriteriaProps) {
  const { t } = useTranslation('validation');

  return (
    <div className={`mt-1 space-y-1 ${className}`}>
      <p className="text-[11px] font-medium text-slate-500">{t('password.title')}</p>
      <ul className="space-y-0.5">
        {PASSWORD_RULES.map((rule) => {
          const met = rule.test(password);
          return (
            <li
              key={rule.key}
              className={`flex items-center gap-1.5 text-[11px] ${
                met ? 'text-emerald-600' : 'text-slate-500'
              }`}
            >
              {met ? (
                <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" strokeWidth={2} />
              ) : (
                <Circle className="w-3.5 h-3.5 text-slate-400 shrink-0" strokeWidth={2} />
              )}
              <span>{t(`password.${rule.key}`)}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

PasswordCriteria.displayName = 'PasswordCriteria';
